"use client";

import { ProductBadges } from "@/ui/components/catalog/product-badges";
import Link from "next/link";
import { useLocale } from "next-intl";
import { ProductQualityNote } from "@/ui/components/catalog/product-quality-note";
import { FavoriteButton } from "@/ui/components/favorite-button";
import type { ProductCardData } from "@/ui/components/plp/product-card";
import { ProductCardCart } from "@/ui/components/plp/product-card-cart";
import type { PeptideMetadata } from "@/lib/peptide-metadata";
import type { PeptideStatus } from "@/lib/research/types";
import { translateResearchLevel } from "@/lib/peptide-translations";
import { PeptideImagePlaceholder } from "@/ui/components/catalog/peptide-image-placeholder";
import Image from "next/image";

export interface PeptideProductCardData extends ProductCardData {
	peptideMetadata: PeptideMetadata | null;
	resolvedStatus?: PeptideStatus | null;
	peptideCode?: string;
}

interface PeptideProductCardProps {
	product: PeptideProductCardData;
	priority?: boolean;
	/** Optional Low-Stock-Hinweis, z.B. "Noch 4 übrig" */
	stockBadge?: string;
}

function CardImage({ product, priority }: { product: PeptideProductCardData; priority: boolean }) {
	const src = product.image !== "/placeholder.svg" ? product.image : null;
	const code = product.peptideCode ?? product.slug;

	if (src) {
		return (
			<div
				className={`product-card-image-swap relative h-full w-full ${product.hoverImage ? "product-card-image-swap--has-hover" : ""}`}
			>
				<Image
					src={src}
					alt={product.imageAlt || product.name}
					fill
					sizes="(max-width: 1024px) 50vw, 25vw"
					className="product-card-image-primary object-contain object-center transition duration-200 ease-out"
					priority={priority}
				/>
				{product.hoverImage && (
					<Image
						src={product.hoverImage}
						alt=""
						fill
						sizes="(max-width: 1024px) 50vw, 25vw"
						className="product-card-image-hover object-contain object-center opacity-0 transition duration-200 ease-out"
					/>
				)}
			</div>
		);
	}

	return <PeptideImagePlaceholder code={code} className="aspect-square" />;
}

export function PeptideProductCard({ product, priority = false, stockBadge }: PeptideProductCardProps) {
	const locale = useLocale();

	const pm = product.peptideMetadata;
	return (
		<article className="hover:border-primary/30 group relative flex h-full flex-col overflow-hidden border border-border bg-card transition-all duration-300 hover:shadow-md">
			{/* Blueprint-Raster overlay (subtle, behind content) */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 z-0 opacity-[0.04]"
				style={{
					backgroundImage:
						"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
					backgroundSize: "24px 24px",
				}}
			/>

			{/* Favorite (Bookmark) — sichtbar Mobile + Computer */}
			<div
				className="absolute right-2.5 top-2.5 z-10"
				onClick={(e) => e.preventDefault()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<FavoriteButton
					slug={product.slug}
					className="bg-background/80 backdrop-blur-sm hover:bg-background"
				/>
			</div>

			<Link href={product.href} className="flex flex-1 flex-col">
				{/* Image */}
				<div className="relative aspect-square overflow-hidden bg-white">
					<CardImage product={product} priority={priority} />

					{/* Floating badges — top-left, stacked (Mobile versteckt) — Status-Badge (Verfügbar/Anfrage) entfernt */}
					{pm?.researchLevel && (
						<div className="absolute left-0 top-2.5 hidden max-w-[min(100%,11rem)] flex-col gap-0.5 pr-1 lg:flex">
							<span className="block truncate border border-l-0 border-border bg-info px-1.5 py-px text-[10px] font-medium tracking-wide text-info-foreground backdrop-blur-sm">
								{translateResearchLevel(pm.researchLevel, locale)}
							</span>
						</div>
					)}

					{/* Score — bottom-right floating */}
					{pm && (
						<div className="absolute bottom-2.5 right-2.5">
							<span className="inline-flex items-center gap-1 border bg-white px-2 py-0.5 font-mono text-[11px] font-bold tabular-nums text-foreground shadow-sm backdrop-blur-sm">
								<span className="h-1.5 w-1.5 rounded-full bg-primary" />
								{pm.scores.overall.toFixed(1)}
							</span>
						</div>
					)}
				</div>

				{/* Info */}
				<div className="flex flex-1 flex-col p-3.5 pt-3">
					{/* Low-Stock-Badge — direkt über Brand/Category-Text */}
					{stockBadge && (
						<span className="mb-1 inline-flex w-fit items-center rounded-full bg-primary-hero px-2 py-0.5 font-mono text-[0.5rem] font-semibold uppercase tracking-[0.14em] text-primary-hero-foreground">
							{stockBadge}
						</span>
					)}

					{/* Brand */}
					{product.brand && (
						<p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
							{product.brand}
						</p>
					)}

					{/* Name */}
					<h3 className="group-hover:text-primary line-clamp-2 text-[15px] font-semibold leading-snug tracking-tight">
						{product.name}
					</h3>

					<ProductQualityNote slug={product.slug} />

					{/* Description */}
					{product.shortDescription && (
						<p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">
							{product.shortDescription}
						</p>
					)}

					{/* Review summary — auf Computer (lg+) in success-grün, Mobile bleibt amber */}
					{product.reviewSummary && product.reviewSummary.count > 0 && (
						<div className="flex items-center gap-1">
							<svg
								className="h-3.5 w-3.5 fill-amber-400 text-amber-400 lg:fill-success lg:text-success"
								viewBox="0 0 20 20"
								aria-hidden="true"
							>
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
							</svg>
							<span className="text-xs font-medium tabular-nums text-foreground lg:text-success">
								{product.reviewSummary.avgRating.toFixed(1)}
							</span>
							<span className="lg:text-success/80 text-xs text-muted-foreground">
								({product.reviewSummary.count})
							</span>
						</div>
					)}

					<ProductBadges
						slug={product.slug}
						categories={pm?.peptideCategories}
						trustBadges={product.trustBadges}
						category={product.category}
					/>

					{/* Price */}
				</div>
			</Link>

			{/* Purchase action */}
			<div className="p-2 sm:p-3">
				<ProductCardCart
					channel={product.channel}
					locale={product.locale}
					productName={product.name}
					variants={product.variants ?? []}
					pricing={product}
				/>
			</div>
		</article>
	);
}
